import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as QRCode from 'qrcode';
import { I18nService } from '../../core/i18n.service';
import { ApiService } from '../../core/api.service';
import { SessionService } from '../../core/session.service';
import { safeExternalUrl } from '../../core/url-security';


@Component({ selector: 'app-security', standalone: true, imports: [FormsModule], templateUrl: './security.html', styleUrl: './security.scss' })
export class Security implements OnInit {
  mfaStatusKey = '';
  passwordResetStatusKey = '';
  mfaSecretCode = '';
  mfaOtpAuthUri = '';
  mfaQrCodeDataUrl = '';
  mfaVerificationCode = '';
  isMfaEnabled = false;
  isLoadingMfaStatus = false;
  isEnablingMfa = false;
  isConfirmingMfa = false;
  isDisablingMfa = false;
  isRequestingPasswordReset = false;
  isConfirmingPasswordReset = false;
  resetCode = '';
  newPassword = '';
  orcidStatusKey = '';
  isLinkingOrcid = false;
  isUnlinkingOrcid = false;
  constructor(public i18n: I18nService, private api: ApiService, public session: SessionService) {}

  async ngOnInit(): Promise<void> {
    await this.refreshMfaStatus();
  }

  async refreshMfaStatus(): Promise<void> {
    if (this.isLoadingMfaStatus) return;
    this.isLoadingMfaStatus = true;
    try {
      const status = await this.api.getSoftwareMfaStatus();
      this.isMfaEnabled = status.enabled;
    } catch {
      this.mfaStatusKey ||= 'security.mfaStatusLoadFailed';
    } finally {
      this.isLoadingMfaStatus = false;
    }
  }

  async enableMfa(): Promise<void> {
    if (this.isEnablingMfa) return;

    this.isEnablingMfa = true;
    this.mfaStatusKey = '';
    this.mfaSecretCode = '';
    this.mfaOtpAuthUri = '';
    this.mfaQrCodeDataUrl = '';
    try {
      const response = await this.api.beginSoftwareMfaSetup();
      this.mfaSecretCode = response.secretCode;
      this.mfaOtpAuthUri = this.buildOtpAuthUri(response.secretCode);
      this.mfaQrCodeDataUrl = await QRCode.toDataURL(this.mfaOtpAuthUri, { errorCorrectionLevel: 'M', margin: 2, width: 220 });
      this.mfaStatusKey = 'security.mfaSetupStarted';
    } catch {
      this.mfaStatusKey = 'security.mfaSetupFailed';
    } finally {
      this.isEnablingMfa = false;
    }
  }

  async confirmMfa(): Promise<void> {
    if (this.isConfirmingMfa) return;

    this.isConfirmingMfa = true;
    this.mfaStatusKey = '';
    try {
      await this.api.confirmSoftwareMfaSetup(this.mfaVerificationCode.trim());
      this.isMfaEnabled = true;
      this.mfaSecretCode = '';
      this.mfaOtpAuthUri = '';
      this.mfaQrCodeDataUrl = '';
      this.mfaVerificationCode = '';
      this.mfaStatusKey = 'security.mfaEnabled';
    } catch {
      this.mfaStatusKey = 'security.mfaConfirmFailed';
    } finally {
      this.isConfirmingMfa = false;
    }
  }

  async disableMfa(): Promise<void> {
    if (this.isDisablingMfa) return;

    this.isDisablingMfa = true;
    this.mfaStatusKey = '';
    try {
      await this.api.disableSoftwareMfa();
      this.isMfaEnabled = false;
      this.mfaSecretCode = '';
      this.mfaOtpAuthUri = '';
      this.mfaQrCodeDataUrl = '';
      this.mfaVerificationCode = '';
      this.mfaStatusKey = 'security.mfaDisabled';
    } catch {
      this.mfaStatusKey = 'security.mfaDisableFailed';
    } finally {
      this.isDisablingMfa = false;
    }
  }

  get primaryMfaActionLabel(): string {
    if (this.isLoadingMfaStatus) return this.i18n.t('security.loadingMfaStatus');
    if (this.isEnablingMfa) return this.i18n.t('security.enablingMfa');
    return this.isMfaEnabled ? this.i18n.t('security.replaceMfaDevice') : this.i18n.t('security.enableMfa');
  }

  async resetPassword(): Promise<void> {
    if (this.isRequestingPasswordReset) return;

    this.isRequestingPasswordReset = true;
    this.passwordResetStatusKey = '';
    try {
      await this.api.requestPasswordReset(this.session.currentUser()?.email ?? '');
      this.passwordResetStatusKey = 'security.passwordResetCodeSent';
    } catch {
      this.passwordResetStatusKey = 'security.passwordResetFailed';
    } finally {
      this.isRequestingPasswordReset = false;
    }
  }

  async confirmPasswordReset(): Promise<void> {
    if (this.isConfirmingPasswordReset) return;

    this.isConfirmingPasswordReset = true;
    this.passwordResetStatusKey = '';
    try {
      await this.api.confirmPasswordReset(this.session.currentUser()?.email ?? '', this.resetCode.trim(), this.newPassword);
      this.passwordResetStatusKey = 'security.passwordResetConfirmed';
      this.resetCode = '';
      this.newPassword = '';
    } catch {
      this.passwordResetStatusKey = 'security.passwordResetConfirmFailed';
    } finally {
      this.isConfirmingPasswordReset = false;
    }
  }

  get isOrcidEnabled(): boolean {
    return this.session.isOrcidEnabled();
  }

  get isOrcidLinked(): boolean {
    return Boolean(this.session.currentUser()?.orcidId?.trim());
  }

  get orcidId(): string {
    return this.session.currentUser()?.orcidId ?? '';
  }

  get orcidUri(): string {
    return this.session.currentUser()?.orcidUri ?? '';
  }

  get safeOrcidUri(): string {
    return safeExternalUrl(this.orcidUri);
  }

  async linkOrcid(): Promise<void> {
    if (!this.isOrcidEnabled || this.isLinkingOrcid) return;
    this.isLinkingOrcid = true;
    this.orcidStatusKey = '';
    try {
      await this.session.linkOrcid();
    } catch {
      this.orcidStatusKey = 'security.orcidLinkFailed';
      this.isLinkingOrcid = false;
    }
  }

  async unlinkOrcid(): Promise<void> {
    if (!this.isOrcidEnabled || this.isUnlinkingOrcid) return;
    this.isUnlinkingOrcid = true;
    this.orcidStatusKey = '';
    try {
      await this.session.unlinkOrcid();
      this.orcidStatusKey = 'security.orcidUnlinked';
    } catch {
      this.orcidStatusKey = 'security.orcidUnlinkFailed';
    } finally {
      this.isUnlinkingOrcid = false;
    }
  }

  private buildOtpAuthUri(secretCode: string): string {
    const issuer = 'PhD Compass';
    const email = this.session.currentUser()?.email || 'user@phdcompass.net';
    const label = `${issuer}:${email}`;
    const params = new URLSearchParams({ secret: secretCode.replace(/\s+/g, ''), issuer, algorithm: 'SHA1', digits: '6', period: '30' });
    return `otpauth://totp/${encodeURIComponent(label)}?${params.toString()}`;
  }
}
